import { useState, useEffect } from 'react';
import { CONFIG, Loading, ReportSignoffDialog, api, enqueueAndDownloadReport, useToast, PanelHowTo } from './_barrel.js';


        // ===== Reports Panel =====
        // Lists prior reports generated for this subject, lets the analyst
        // generate a new draft, sign off existing drafts, and re-download
        // signed reports.
        function ReportsPanel({ token, subjectId, subject }) {
            const [reports, setReports] = useState(null);
            const [error, setError] = useState(null);
            const [expanded, setExpanded] = useState(false);
            const [generating, setGenerating] = useState(false);
            const [genStatus, setGenStatus] = useState(null);    // progress text while rendering
            const [signoffFor, setSignoffFor] = useState(null);  // report object being signed
            const toast = useToast();

            const reload = async () => {
                try {
                    const r = await api(`/api/v1/subjects/${subjectId}/reports`, {}, token);
                    setReports(r);
                } catch (e) { setError(e.message); }
            };
            useEffect(() => { reload(); }, [token, subjectId]);

            const generateNewReport = async () => {
                setGenerating(true);
                setError(null);
                setGenStatus('Queuing…');
                try {
                    // Queued and rendered off the request path — poll, then
                    // download the stored PDF and refresh the reports list.
                    await enqueueAndDownloadReport(token, subjectId, { onStatus: setGenStatus });
                    toast('Draft report generated. Download starting.');
                    await reload();
                } catch (e) {
                    setError(e.message);
                } finally { setGenerating(false); setGenStatus(null); }
            };

            const downloadReport = async (report) => {
                try {
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/reports/${report.id}/pdf`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${report.report_number || report.id}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                } catch (e) { setError(e.message); }
            };

            const draftCount = (reports || []).filter(r => !r.signed_off_at).length;
            const signedCount = (reports || []).filter(r => r.signed_off_at).length;

            return (
                <>
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer"
                         onClick={() => setExpanded(!expanded)}>
                        <div>
                            <div className="font-display text-xl gold">Reports</div>
                            <div className="text-xs text-muted font-mono mt-1">
                                {reports == null ? 'Loading…' :
                                 reports.length === 0 ? 'No reports yet — generate the first draft' :
                                 `${signedCount} signed · ${draftCount} draft · ${reports.length} total`}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button type="button" className="btn-gold"
                                onClick={(e) => { e.stopPropagation(); generateNewReport(); }}
                                disabled={generating}>
                                {generating ? (genStatus || 'Generating…') : 'Generate Draft Report'}
                            </button>
                            <div className="text-xs text-muted">{expanded ? '▾' : '▸'}</div>
                        </div>
                    </div>
                    <PanelHowTo id="reports" />

                    {expanded && (
                        <div className="px-6 py-5">
                            {error && <div className="text-[var(--red)] text-sm mb-3">{error}</div>}
                            {reports == null ? (
                                <div className="text-muted text-sm">Loading…</div>
                            ) : reports.length === 0 ? (
                                <div className="text-muted text-sm">No reports yet. Click Generate Draft Report.</div>
                            ) : (
                                <div className="space-y-2">
                                    {reports.map(r => {
                                        const signed = !!r.signed_off_at;
                                        return (
                                            <div key={r.id} className="flex justify-between items-start gap-4 py-2 border-b border-veil last:border-b-0">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm">
                                                        <span className="font-mono">{r.report_number || r.id.slice(0,8)}</span>
                                                        {signed ? (
                                                            <span className="ml-2 gold">· SIGNED</span>
                                                        ) : (
                                                            <span className="ml-2 text-[var(--red)]">· DRAFT</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted font-mono mt-1">
                                                        generated {new Date(r.generated_at).toLocaleString('en-GB')} by {r.generated_by_display_name}
                                                    </div>
                                                    {signed && (
                                                        <div className="text-xs text-muted font-mono mt-1">
                                                            signed off {new Date(r.signed_off_at).toLocaleString('en-GB')} by {r.signed_off_by_display_name}
                                                            {r.signed_off_by_role && ` (${r.signed_off_by_role})`}
                                                            {r.signoff_classification && ` · ${r.signoff_classification.replace('_',' ')}`}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button type="button" className="btn-ghost"
                                                        onClick={() => downloadReport(r)}
                                                        style={{fontSize:'11px', padding:'5px 12px'}}>
                                                        Download
                                                    </button>
                                                    {!signed && (
                                                        <button type="button" className="btn-gold"
                                                            onClick={() => setSignoffFor(r)}
                                                            style={{fontSize:'11px', padding:'5px 12px'}}>
                                                            Sign Off →
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {signoffFor && (
                    <ReportSignoffDialog
                        token={token}
                        report={signoffFor}
                        onClose={() => setSignoffFor(null)}
                        onSuccess={() => { setSignoffFor(null); reload(); }}
                    />
                )}
                </>
            );
        }

export { ReportsPanel };
